// useWebsiteScraper.ts — lógica de scraping web de la WorkspacePage.
// Qué hace: maneja el scrape de marca (URL → brandInfo + imágenes), el scrape
//   "solo imágenes", la selección de imágenes encontradas y el alta de las
//   seleccionadas como assets o backgrounds (delegando en los handlers que
//   recibe). Mantiene exactamente el comportamiento que tenía WorkspacePage.
// Qué exporta: hook useWebsiteScraper.
// Quién lo usa: pages/WorkspacePage.tsx (+ components/workspace/*Section).
import { useState } from 'react';
import { BrandInfo } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';

interface ScrapeResponse extends BrandInfo {
  scrapedImages?: string[];
}

interface UseWebsiteScraperParams {
  workspaceId: string | undefined;
  setBrandInfo: (info: BrandInfo) => void;
  // Alta de las imágenes seleccionadas como assets (proviene de useAssetManagement).
  addSelectedAsAssets: (selectedImages: Set<string>) => Promise<void>;
  // Alta de las imágenes seleccionadas como backgrounds (proviene de useBackgroundImageManagement).
  addSelectedAsBackgrounds: (selectedImages: Set<string>, onSuccess: () => void) => Promise<void>;
  // Flag de "agregando background" (proviene de useBackgroundImageManagement) para
  //   deshabilitar los botones de forma cruzada, igual que antes.
  isAddingSelectedAsBackgrounds: boolean;
}

export function useWebsiteScraper({
  workspaceId,
  setBrandInfo,
  addSelectedAsAssets,
  addSelectedAsBackgrounds,
  isAddingSelectedAsBackgrounds,
}: UseWebsiteScraperParams) {
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [scrapedImages, setScrapedImages] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isAddingSelectedImages, setIsAddingSelectedImages] = useState(false);
  const [imageScrapeUrl, setImageScrapeUrl] = useState('');
  const [isScrapingImages, setIsScrapingImages] = useState(false);
  // Feedback del último scrape-only: null = nunca corrió; string = mensaje a mostrar.
  const [imageScrapeMessage, setImageScrapeMessage] = useState<string | null>(null);

  const handleScrape = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsScraping(true);
    setScrapedImages([]);
    setSelectedImages(new Set());

    try {
      const data = await apiClient.post<ScrapeResponse>(`/api/workspaces/${workspaceId}/scrape`, {
        url: scrapeUrl,
      });
      setBrandInfo(data);
      setScrapeUrl('');
      // Store scraped images for selection
      if (data.scrapedImages && data.scrapedImages.length > 0) {
        setScrapedImages(data.scrapedImages);
      }
    } catch (error) {
      console.error('Failed to scrape:', error);
    } finally {
      setIsScraping(false);
    }
  };

  const toggleImageSelection = (imageUrl: string) => {
    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageUrl)) {
        newSet.delete(imageUrl);
      } else {
        newSet.add(imageUrl);
      }
      return newSet;
    });
  };

  const handleScrapeImagesOnly = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageScrapeUrl) return;

    setIsScrapingImages(true);
    setScrapedImages([]);
    setSelectedImages(new Set());
    setImageScrapeMessage(null);

    try {
      const data = await apiClient.post<{ images: string[]; url: string }>(
        `/api/workspaces/${workspaceId}/scrape-images`,
        { url: imageScrapeUrl }
      );
      if (data.images && data.images.length > 0) {
        setScrapedImages(data.images);
        setImageScrapeMessage(null);
      } else {
        // El sitio respondió pero no encontramos fotos usables (muchas webs cargan
        // imágenes por JS o las sirven muy chicas y las filtramos).
        setImageScrapeMessage('No usable images found on that page. Try a different page (e.g. an "about" or product page), or add a URL manually above.');
      }
      setImageScrapeUrl('');
    } catch (error) {
      console.error('Failed to scrape images:', error);
      setImageScrapeMessage(error instanceof Error ? `Could not scan that site: ${error.message}` : 'Could not scan that site.');
    } finally {
      setIsScrapingImages(false);
    }
  };

  const handleAddSelectedImages = async () => {
    if (selectedImages.size === 0) return;
    setIsAddingSelectedImages(true);
    try {
      await addSelectedAsAssets(selectedImages);
      setSelectedImages(new Set());
      setScrapedImages([]);
    } catch (error) {
      console.error('Failed to add selected images:', error);
    } finally {
      setIsAddingSelectedImages(false);
    }
  };

  const handleAddSelectedAsBackgrounds = async () => {
    if (selectedImages.size === 0) return;
    await addSelectedAsBackgrounds(selectedImages, () => {
      setSelectedImages(new Set());
      setScrapedImages([]);
    });
  };

  const dismissScrapedImages = () => {
    setScrapedImages([]);
    setSelectedImages(new Set());
    setImageScrapeMessage(null);
  };

  return {
    scrapeUrl,
    setScrapeUrl,
    isScraping,
    scrapedImages,
    selectedImages,
    isAddingSelectedImages,
    imageScrapeUrl,
    setImageScrapeUrl,
    isScrapingImages,
    imageScrapeMessage,
    isAddingSelectedAsBackgrounds,
    handleScrape,
    toggleImageSelection,
    handleScrapeImagesOnly,
    handleAddSelectedImages,
    handleAddSelectedAsBackgrounds,
    dismissScrapedImages,
  };
}
